import { BaseTexture } from '@babylonjs/core/Materials/Textures/baseTexture';
import { Material } from '@babylonjs/core/Materials/material';
import { Node } from '@babylonjs/core/node';
import { Scene } from '@babylonjs/core/scene';
import { Kb3dObject, Token, instanceOf } from '@kb3d/models/classes';
import { ITrackedClass } from '@kb3d/models/interfaces/tracked-class';
import { SyncService } from '@kb3d/models/services';
import { KbViewer } from '@kb3d/view/kb-viewer';

export abstract class Renderer<T extends Kb3dObject, T3D extends Node | Material | BaseTexture> {
    constructor(protected viewer: KbViewer, protected scene: Scene) {}

    public babylonMap = new Map<string, T3D>();

    /**
     * The function to update the renderer scene for the given renderer type
     * @param node The node to update
     * @returns nothing
     */
    protected abstract update3dInternal(node: ITrackedClass<T>): void;

    /**
     * Token that defines the type or base type of model that this
     * renderer processes
     */
    public abstract modelToken: Token<T>;

    /**
     * Determines whether the given node should be handled by this renderer.  Can be
     * overriden for special behavior
     */
    public handlesNode(node: Kb3dObject): boolean {
        return instanceOf(node, this.modelToken);
    }

    public update3d(node: ITrackedClass<T>) {
        this.update3dInternal(node);
        if (this.viewer.options.validator) {
            this.viewer.options.validator.validateNode(node);
        }
    }

    public delete3d(node: ITrackedClass<T>) {
        const o = this.babylonMap.get(node._dynamicId);
        if (o) {
            if (o instanceof Material) {
                o.dispose(true, true);
            } else {
                o.dispose();
            }
        }
        this.babylonMap.delete(node._dynamicId);

        SyncService.nodeDeletionProcessed(node);
    }

    public get3d(node: ITrackedClass<T> | T): T3D | undefined {
        return this.babylonMap.get(node._dynamicId);
    }

    public set3d(node: ITrackedClass<T> | T, babylonObject: T3D) {
        this.babylonMap.set(node._dynamicId, babylonObject);
    }

    public getChanges(node: ITrackedClass<T>) {
        const isNew = this.get3d(node) == null;
        const changes = new Map(node.changes);
        if (isNew) {
            for (const prop in node) {
                changes.set(prop as Extract<keyof T, string>, node[prop as keyof T]);
            }
        }
        return changes;
    }

    public dispose() {
        this.babylonMap.clear();
    }
}
